import * as React from 'react';
import "./Body.scss";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ChatItem from '../chatItem/ChatItem';
import { ChatItemDefines, ChatItemProps, ChatItemPropsCore } from '../chatItem/IChatItem';
import { BodyDefines, BodyProps } from './IBody';
import { ColorDefines } from '../../interface/IColors';
import { ProgressState } from '../../common/enum/progressState';
import merge from '../../util/merge';
import { EventDefines } from '../../interface/IEvent';

interface Body {
  props: BodyProps;
  defines: BodyDefines;
  colors: ColorDefines;
}

interface ContentsProps {
  props: ChatItemProps;
  defines: ChatItemDefines;
}

const Body = ({ props, defines, colors }: Body) => {
  const ref = useRef<HTMLDivElement>();
  // console.debug("body")
  const selectProgressState = useCallback((index: number, step: number) => {
    if (index == step) return ProgressState.CURRENT;
    if (index < step) return ProgressState.AFTER;
    return ProgressState.BEFORE;
  }, []);
  const contentsCore = useMemo<{ core: ChatItemPropsCore, def: ChatItemDefines }[]>(() => {
    // console.debug("Body contentsCore")
    return defines.chat.filter(v => v)
      .map((def: ChatItemDefines, index: number) => {
        const person = def.sender ? props.persons[def.sender] : props.persons.default;
        const events = merge<EventDefines>(def.event, defines.event);
        const core = {
          index, person, events,
          setStep: props.setStep,
          collapseCallback: props.collapseCallback,
          option: defines.option
        };
        return { core, def };
      });
  }, [
    defines.chat,
    props.collapseCallback,
    props.setStep
  ]);

  const contents = useMemo<ContentsProps[]>(() => {
    // console.debug("Body contents")
    return contentsCore
      .map(({ core, def }: { core: ChatItemPropsCore, def: ChatItemDefines }) => {
        const chatItemProps: ChatItemProps = {
          ...core,
          ts: props.ts, hidden: core.index > props.step,
          progressState: selectProgressState(core.index, props.step),
        };
        return {
          props: chatItemProps,
          defines: def
        }
      });
  }, [defines.chat, props.step]);

  const step = useMemo(() => Math.min(props.step, (contents || []).length - 1), [contents, props.step]);

  useEffect(() => {
    setTimeout(() => {
      const scrollBottom = ref.current.scrollHeight;
      ref.current.scrollTo(0, scrollBottom - 1);
    }, 500);
  }, [step]);

  useEffect(() => {
    if (!props.touched) return;
    if (step < 0) {
      setTimeout(() => props.setStep(step + 1), 1000);
      return;
    }
    const nowStepChat = contents[step].defines;
    if (!nowStepChat.stop) {
      setTimeout(() => props.setStep(step + 1), 
        null != nowStepChat.interval ? nowStepChat.interval : 1500);
    }
  }, [props.touched, step]);
  
  return (
    <div className="body">
      <div className="log-viewer" ref={ref}>
        <div className="inner">
          {contents && contents.map((c, i) => (
            <ChatItem
              key={i}
              defines={c.defines}
              props={c.props}
              colors={colors}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default Body;
